import React from 'react';
import { Col, Row, Statistic, Typography } from 'antd';
import type { Quotation } from '@/services/quotation.service';
import { formatMoney, formatPercent, UAE_MONEY_PROFILE } from '@/utils/format';

const { Text } = Typography;

type Props = {
  quotation: Quotation;
  compact?: boolean;
};

function toNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function lineLoadedCost(item: {
  loaded_cost_amount?: number;
  base_cost_amount?: number;
  allocated_overhead_amount?: number;
}): number {
  const loaded = toNum(item.loaded_cost_amount);
  if (loaded > 0) return loaded;
  return toNum(item.base_cost_amount) + toNum(item.allocated_overhead_amount);
}

export const QuotationCommercialHero: React.FC<Props> = ({ quotation, compact }) => {
  const currency = quotation.currency || 'AED';
  // Display sell as grand total (customer-facing). Margin uses ex-VAT sell when available
  // so VAT is not treated as profit (matches backend profitability snapshot).
  const sellDisplay = toNum(quotation.grand_total);
  const sellForMargin =
    toNum(quotation.total_after_discount) ||
    toNum(quotation.subtotal) ||
    sellDisplay;
  const itemsCost = (quotation.items || []).reduce((s, i) => s + lineLoadedCost(i), 0);
  const costFromItems = itemsCost > 0 ? itemsCost : null;
  const cost =
    quotation.profitability_snapshot?.loaded_total_cost != null
      ? toNum(quotation.profitability_snapshot.loaded_total_cost)
      : costFromItems;
  // Always derive margin from current sell − cost when cost is known. Snapshot gross_profit
  // can stay stuck at 0 after BOQ convert (sell later raised) because persist used to no-op on existing rows.
  const margin = cost != null ? Math.round((sellForMargin - cost) * 100) / 100 : null;
  const marginPct = margin != null && sellForMargin > 0 ? (margin / sellForMargin) * 100 : null;
  // Markup % = (sell − cost) / cost — same convention as Trading & Rental Estimation.
  const markupPct = margin != null && cost != null && cost > 0 ? (margin / cost) * 100 : null;

  const stats = [
    { title: 'Sell', value: formatMoney(sellDisplay || sellForMargin, { ...UAE_MONEY_PROFILE, currency }) },
    {
      title: 'Cost',
      value: cost != null ? formatMoney(cost, { ...UAE_MONEY_PROFILE, currency }) : '—',
    },
    {
      title: 'Margin',
      value: margin != null ? formatMoney(margin, { ...UAE_MONEY_PROFILE, currency }) : '—',
    },
    {
      title: 'Margin %',
      value:
        marginPct != null
          ? formatPercent(marginPct, { semantics: 'whole', maximumFractionDigits: 2 })
          : '—',
    },
    {
      title: 'Markup %',
      value:
        markupPct != null
          ? formatPercent(markupPct, { semantics: 'whole', maximumFractionDigits: 2 })
          : '—',
    },
    {
      title: 'Discount',
      value: formatPercent(toNum(quotation.discount_percentage), {
        semantics: 'whole',
        maximumFractionDigits: 2,
      }),
    },
    {
      title: 'Validity',
      value: quotation.validity_days ? `${quotation.validity_days} days` : '—',
    },
  ];

  return (
    <div
      style={{
        padding: compact ? '12px 0' : '16px 0',
        borderBottom: '1px solid #f0f0f0',
        marginBottom: 16,
      }}
    >
      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Commercial Summary
      </Text>
      <Row gutter={[16, 12]} style={{ marginTop: 8 }}>
        {stats.map((s) => (
          <Col key={s.title} xs={12} sm={8} md={3} lg={stats.length > 6 ? 3 : 4}>
            <Statistic title={s.title} value={s.value} valueStyle={{ fontSize: compact ? 16 : 18 }} />
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default QuotationCommercialHero;
